import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Renderizador minimo de texto con formato.
 *
 * El asistente responde con negritas, listas, saltos de linea y enlaces a
 * pantallas de la app («→ Cargar ventas», rediseño de oct 2026). Con eso basta,
 * asi que no entra ninguna libreria de markdown: se parsea a mano y se pinta
 * con elementos de React. Nada de `dangerouslySetInnerHTML`, de modo que el
 * texto del modelo no puede inyectar HTML aunque lo intente.
 */

type Inline =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'code'; value: string }
  | { kind: 'link'; value: string; to: string };

type Block =
  | { kind: 'p'; lines: string[] }
  | { kind: 'ul'; items: string[] }
  | { kind: 'ol'; items: string[] };

const INLINE = /\*\*([^*]+)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

/**
 * Solo rutas internas de la app: «/ingresos», «/recetas/abc». Nunca una URL
 * completa ni un protocolo: el modelo no puede mandar a nadie fuera.
 */
const RUTA_INTERNA = /^\/[a-z][a-z0-9-]*(\/[\w-]+)*\/?(\?[\w=&%-]*)?$/;

/** Exportada solo para las pruebas. */
export { parseInline };

/** Trocea una linea en texto normal, negritas y fragmentos monoespaciados. */
function parseInline(line: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;

  for (const match of line.matchAll(INLINE)) {
    const index = match.index ?? 0;
    if (index > last) out.push({ kind: 'text', value: line.slice(last, index) });
    if (match[1] !== undefined) out.push({ kind: 'bold', value: match[1] });
    else if (match[2] !== undefined) out.push({ kind: 'code', value: match[2] });
    else if (match[3] !== undefined && match[4] !== undefined) {
      // Un enlace que no es una ruta interna se queda en su texto, sin enlace.
      if (RUTA_INTERNA.test(match[4])) out.push({ kind: 'link', value: match[3], to: match[4] });
      else out.push({ kind: 'text', value: match[3] });
    }
    last = index + match[0].length;
  }

  if (last < line.length) out.push({ kind: 'text', value: line.slice(last) });
  return out;
}

function renderInline(line: string, keyPrefix: string): ReactNode[] {
  return parseInline(line).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    // Dentro de la negrita puede ir un enlace: «Ve a **[Ver gastos](/gastos)**»
    // salia en crudo (Miguel, 21 oct 2026). Sin `**` dentro, no hay bucle.
    if (part.kind === 'bold') return <strong key={key}>{renderInline(part.value, key)}</strong>;
    if (part.kind === 'code') return <code key={key}>{part.value}</code>;
    if (part.kind === 'link') return <Link key={key} className="rich__link" to={part.to}>{part.value}</Link>;
    return <Fragment key={key}>{part.value}</Fragment>;
  });
}

const BULLET = /^[-*•]\s+/;
const NUMBERED = /^\d+[.)]\s+/;

/** Agrupa las lineas en parrafos y listas. Una linea vacia cierra el bloque. */
/** Exportada solo para poder probar el troceado sin montar React. */
export function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  let open = false;

  /*
   * Una linea EN BLANCO entre dos puntos no rompe la lista.
   *
   * Antes si: el blanco cerraba el bloque y el punto siguiente abria un <ol>
   * nuevo, asi que una lista de tres pasos se pintaba como tres listas de un
   * paso y salia "1. 1. 1." en vez de "1. 2. 3.". El modelo deja esos blancos
   * continuamente cuando los puntos empiezan en negrita, que es justo el
   * formato de las recomendaciones de Alexia.
   *
   * Solo se pega al bloque anterior si es una lista DEL MISMO TIPO: con un
   * parrafo por medio son dos listas distintas y la segunda vuelve a empezar
   * por 1, que es lo correcto.
   */
  const anyadeALista = (kind: 'ul' | 'ol', item: string) => {
    const previo = blocks[blocks.length - 1];
    if (previo && previo.kind === kind) previo.items.push(item);
    else blocks.push({ kind, items: [item] });
  };

  for (const raw of String(text ?? '').split('\n')) {
    const line = raw.trim();

    if (line === '') {
      open = false;
      continue;
    }

    const current = open ? blocks[blocks.length - 1] : undefined;

    if (BULLET.test(line)) {
      anyadeALista('ul', line.replace(BULLET, ''));
      open = true;
      continue;
    }

    if (NUMBERED.test(line)) {
      anyadeALista('ol', line.replace(NUMBERED, ''));
      open = true;
      continue;
    }

    // Los encabezados de markdown se quedan en parrafo normal: en esta
    // pantalla una jerarquia de titulos no aporta nada.
    const clean = line.replace(/^#{1,6}\s+/, '');
    if (current && current.kind === 'p') current.lines.push(clean);
    else blocks.push({ kind: 'p', lines: [clean] });
    open = true;
  }

  return blocks;
}

/** Texto del asistente con formato basico y seguro. */
export function RichText({ text }: { text: string }): JSX.Element {
  const blocks = parseBlocks(text);

  if (blocks.length === 0) {
    return <p className="rich__p">Alexia no ha devuelto ningún texto para esta pregunta.</p>;
  }

  return (
    <div className="rich">
      {blocks.map((block, index) => {
        if (block.kind === 'ul') {
          return (
            <ul className="rich__list" key={`b${index}`}>
              {block.items.map((item, i) => (
                <li key={`b${index}-${i}`}>{renderInline(item, `b${index}-${i}`)}</li>
              ))}
            </ul>
          );
        }

        if (block.kind === 'ol') {
          return (
            <ol className="rich__list" key={`b${index}`}>
              {block.items.map((item, i) => (
                <li key={`b${index}-${i}`}>{renderInline(item, `b${index}-${i}`)}</li>
              ))}
            </ol>
          );
        }

        return (
          <p className="rich__p" key={`b${index}`}>
            {block.lines.map((line, i) => (
              <Fragment key={`b${index}-${i}`}>
                {i > 0 && <br />}
                {renderInline(line, `b${index}-${i}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
